import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scheduleService } from '@/services/schedule.service';
import { toDateKeyTz } from '@/lib/tz';
import type { TaskRow, EventRow } from '@/lib/schedule';

/**
 * Options for `useScheduleWindow`.
 */
export interface UseScheduleWindowOptions {
  /**
   * The ACTIVE household. Required, not optional: RLS alone would serve every
   * household the user belongs to, so a missing id here silently merges two
   * families' schedules into one feed.
   */
  householdId: string;
  initialDate: Date;
  userTimezone: string | null;
  /** Days each side of initialDate to preload on mount. Default: 14 back, 30 forward. */
  initialBackDays?: number;
  initialForwardDays?: number;
}

/**
 * Result returned by `useScheduleWindow`.
 */
export interface UseScheduleWindowResult {
  tasks: TaskRow[];
  events: EventRow[];
  /** Earliest date currently loaded (YYYY-MM-DD), or null while still empty. */
  loadedStart: string | null;
  /** Latest date currently loaded (YYYY-MM-DD), or null while still empty. */
  loadedEnd: string | null;
  /** Initial load is in flight. After the first resolve, this becomes false forever. */
  isLoading: boolean;
  /** A subsequent extend/ensure is in flight. */
  isExtending: boolean;
  error: Error | null;
  /** Extend the window N days into the past. Idempotent if already covered. */
  extendBack: (days: number) => Promise<void>;
  /** Extend the window N days into the future. Idempotent if already covered. */
  extendForward: (days: number) => Promise<void>;
  /** Make sure [startKey, endKey] is fully covered, fetching only the gap. Idempotent. */
  ensureRange: (startKey: string, endKey: string) => Promise<void>;
  /** Refetch everything currently loaded (used after a mutation). */
  reload: () => Promise<void>;
}

interface Interval {
  start: string;
  end: string;
}

const DEFAULT_BACK_DAYS = 14;
const DEFAULT_FORWARD_DAYS = 30;

/**
 * Format a Date into a YYYY-MM-DD key using local calendar fields.
 * Mirrors the inline `toDateKey` in `ScheduleTab.tsx`.
 */
function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse a YYYY-MM-DD key into a local Date at midnight. Avoids `new Date(key)`
 * which would parse as UTC and shift the day in negative-offset timezones.
 */
function fromDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map((s) => Number(s));
  return new Date(y, m - 1, d);
}

/** Shift a YYYY-MM-DD key by N days (positive = forward, negative = back). */
function shiftDateKey(key: string, days: number): string {
  const d = fromDateKey(key);
  d.setDate(d.getDate() + days);
  return toDateKey(d);
}

/** Compare two date keys (YYYY-MM-DD); negative if a<b, 0 if equal, positive if a>b. */
function cmpKey(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Merge a new interval into a sorted, non-overlapping interval list.
 * Intervals are inclusive on both ends and adjacency counts as overlap
 * (e.g. [..., '2026-01-10'] and ['2026-01-11', ...] collapse into one).
 */
function mergeInterval(intervals: Interval[], add: Interval): Interval[] {
  const merged: Interval[] = [];
  let curStart = add.start;
  let curEnd = add.end;
  let inserted = false;

  for (const iv of intervals) {
    if (inserted) {
      merged.push(iv);
      continue;
    }
    // No overlap or adjacency: existing strictly before new
    if (cmpKey(shiftDateKey(iv.end, 1), curStart) < 0) {
      merged.push(iv);
      continue;
    }
    // No overlap: existing strictly after new
    if (cmpKey(iv.start, shiftDateKey(curEnd, 1)) > 0) {
      merged.push({ start: curStart, end: curEnd });
      merged.push(iv);
      inserted = true;
      continue;
    }
    // Overlap/adjacent → fold
    if (cmpKey(iv.start, curStart) < 0) curStart = iv.start;
    if (cmpKey(iv.end, curEnd) > 0) curEnd = iv.end;
  }
  if (!inserted) merged.push({ start: curStart, end: curEnd });
  return merged;
}

/**
 * Compute the gap pieces of [start, end] not yet covered by `intervals`.
 * Returns 0, 1, or 2 sub-ranges (or more, if there are multiple holes).
 */
function computeGap(
  intervals: Interval[],
  start: string,
  end: string,
): Interval[] {
  if (cmpKey(start, end) > 0) return [];
  const gaps: Interval[] = [];
  let cursor = start;
  for (const iv of intervals) {
    if (cmpKey(iv.end, cursor) < 0) continue;
    if (cmpKey(iv.start, end) > 0) break;
    if (cmpKey(iv.start, cursor) > 0) {
      gaps.push({ start: cursor, end: shiftDateKey(iv.start, -1) });
    }
    const next = shiftDateKey(iv.end, 1);
    if (cmpKey(next, cursor) > 0) cursor = next;
    if (cmpKey(cursor, end) > 0) break;
  }
  if (cmpKey(cursor, end) <= 0) {
    gaps.push({ start: cursor, end });
  }
  return gaps;
}

/**
 * Windowed schedule cache hook.
 *
 * Owns a merged-intervals array of loaded date ranges and a deduplicated
 * `tasks` + `events` state. Exposes `extendBack`, `extendForward`,
 * `ensureRange`, and `reload`.
 *
 * Designed to back the infinite-scroll Schedule tab where the rendered
 * window grows in both directions and previously loaded items must not
 * be evicted.
 */
export function useScheduleWindow(
  options: UseScheduleWindowOptions,
): UseScheduleWindowResult {
  const {
    householdId,
    initialDate,
    userTimezone,
    initialBackDays = DEFAULT_BACK_DAYS,
    initialForwardDays = DEFAULT_FORWARD_DAYS,
  } = options;

  const [taskMap, setTaskMap] = useState<Map<string, TaskRow>>(() => new Map());
  const [eventMap, setEventMap] = useState<Map<string, EventRow>>(
    () => new Map(),
  );
  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExtending, setIsExtending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Hold a live ref to the latest intervals so async callbacks see the
  // current set without needing to be re-created on every change.
  const intervalsRef = useRef<Interval[]>([]);
  intervalsRef.current = intervals;

  // Track whether the initial mount fetch has resolved.
  const initialDoneRef = useRef(false);

  /**
   * Core fetch: pull tasks + events for a sub-range and fold them into
   * the deduped maps. Does NOT touch `intervals`; the caller decides
   * whether/how to extend the merged-interval set.
   */
  const fetchRange = useCallback(
    async (start: string, end: string) => {
      const [taskData, eventData] = await Promise.all([
        scheduleService.getTasksForRange(householdId, start, end, userTimezone),
        scheduleService.getEventsForRange(
          householdId,
          start,
          end,
          userTimezone,
        ),
      ]);
      return {
        tasks: taskData as TaskRow[],
        events: eventData as EventRow[],
      };
    },
    [householdId, userTimezone],
  );

  /**
   * Ensure `[startKey, endKey]` is fully covered. Computes the gap against
   * the current interval set, fetches only the missing sub-ranges in
   * parallel, and folds results in atomically. On failure, state is
   * left untouched and the promise rejects.
   */
  const ensureRange = useCallback(
    async (startKey: string, endKey: string) => {
      if (cmpKey(startKey, endKey) > 0) return;
      const currentIntervals = intervalsRef.current;
      const gaps = computeGap(currentIntervals, startKey, endKey);
      if (gaps.length === 0) return;

      const firstLoad = !initialDoneRef.current;
      if (firstLoad) setIsLoading(true);
      else setIsExtending(true);
      setError(null);

      try {
        const results = await Promise.all(
          gaps.map((g) => fetchRange(g.start, g.end)),
        );

        // Atomic merge into state: union all fetched rows into maps,
        // merge all gap intervals into the interval set.
        setTaskMap((prev) => {
          const next = new Map(prev);
          for (const r of results) {
            for (const t of r.tasks) next.set(t.id, t);
          }
          return next;
        });
        setEventMap((prev) => {
          const next = new Map(prev);
          for (const r of results) {
            for (const e of r.events) next.set(e.id, e);
          }
          return next;
        });
        setIntervals((prev) => {
          let merged = prev;
          for (const g of gaps) merged = mergeInterval(merged, g);
          intervalsRef.current = merged;
          return merged;
        });
      } catch (err) {
        const wrapped =
          err instanceof Error ? err : new Error('Unable to load schedule');
        setError(wrapped);
        if (firstLoad) setIsLoading(false);
        else setIsExtending(false);
        throw wrapped;
      }

      if (firstLoad) {
        initialDoneRef.current = true;
        setIsLoading(false);
      } else {
        setIsExtending(false);
      }
    },
    [fetchRange],
  );

  const extendBack = useCallback(
    async (days: number) => {
      if (days <= 0) return;
      const current = intervalsRef.current;
      const anchor =
        current.length > 0 ? current[0].start : toDateKey(initialDate);
      const newStart = shiftDateKey(anchor, -days);
      // We want everything from newStart up to (but not duplicating) the
      // current loaded start. ensureRange's gap computation handles dedup.
      const end =
        current.length > 0 ? current[0].start : shiftDateKey(anchor, -1);
      await ensureRange(newStart, end);
    },
    [ensureRange, initialDate],
  );

  const extendForward = useCallback(
    async (days: number) => {
      if (days <= 0) return;
      const current = intervalsRef.current;
      const anchor =
        current.length > 0
          ? current[current.length - 1].end
          : toDateKey(initialDate);
      const newEnd = shiftDateKey(anchor, days);
      const start =
        current.length > 0
          ? current[current.length - 1].end
          : shiftDateKey(anchor, 1);
      await ensureRange(start, newEnd);
    },
    [ensureRange, initialDate],
  );

  /**
   * Refetch every interval currently loaded — used after a mutation
   * (create/update/delete/toggle) that may have added or removed rows
   * inside the loaded window.
   */
  // Reloads overlap (a completion's refresh racing an Undo's, two quick
  // completions): only the newest may write, or an older response landing
  // last resurrects a task that was just completed.
  const reloadSeqRef = useRef(0);
  const reload = useCallback(async () => {
    const current = intervalsRef.current;
    if (current.length === 0) return;
    const seq = ++reloadSeqRef.current;

    setIsExtending(true);
    setError(null);
    try {
      const results = await Promise.all(
        current.map((iv) => fetchRange(iv.start, iv.end)),
      );
      if (seq !== reloadSeqRef.current) return;

      // Replace contents of the loaded window. Rows outside the union
      // shouldn't exist (interval set is the source of truth), so we
      // can safely rebuild from scratch.
      const nextTasks = new Map<string, TaskRow>();
      const nextEvents = new Map<string, EventRow>();
      for (const r of results) {
        for (const t of r.tasks) nextTasks.set(t.id, t);
        for (const e of r.events) nextEvents.set(e.id, e);
      }
      setTaskMap(nextTasks);
      setEventMap(nextEvents);
    } catch (err) {
      // Superseded: the newer reload owns the error and spinner state.
      if (seq !== reloadSeqRef.current) return;
      const wrapped =
        err instanceof Error ? err : new Error('Unable to load schedule');
      setError(wrapped);
      setIsExtending(false);
      throw wrapped;
    }
    setIsExtending(false);
  }, [fetchRange]);

  // Initial mount load.
  useEffect(() => {
    const anchor = toDateKey(initialDate);
    const start = shiftDateKey(anchor, -initialBackDays);
    const end = shiftDateKey(anchor, initialForwardDays);
    // ensureRange is allowed to throw; the rejection is already surfaced
    // through `error` state, so we just silently swallow here.
    ensureRange(start, end).catch(() => {});
    // We intentionally only run this on mount: subsequent prop changes
    // are handled by explicit extend/ensure calls from the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Memoized task array sorted by `due_date` (nulls last). Tasks without
   * a due date still need to appear; rendering decides where to slot them.
   */
  const tasks = useMemo(() => {
    const arr = Array.from(taskMap.values());
    arr.sort((a, b) => {
      const ak = a.due_date ? toDateKeyTz(a.due_date, userTimezone) : '￿';
      const bk = b.due_date ? toDateKeyTz(b.due_date, userTimezone) : '￿';
      if (ak !== bk) return ak < bk ? -1 : 1;
      // Stable secondary by id for deterministic order.
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    return arr;
  }, [taskMap, userTimezone]);

  const events = useMemo(() => {
    const arr = Array.from(eventMap.values());
    arr.sort((a, b) => {
      const ak = toDateKeyTz(a.start_date, userTimezone);
      const bk = toDateKeyTz(b.start_date, userTimezone);
      if (ak !== bk) return ak < bk ? -1 : 1;
      // Fall back to raw start_date for intra-day order, then id.
      if (a.start_date !== b.start_date) {
        return a.start_date < b.start_date ? -1 : 1;
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    return arr;
  }, [eventMap, userTimezone]);

  const loadedStart = intervals.length > 0 ? intervals[0].start : null;
  const loadedEnd =
    intervals.length > 0 ? intervals[intervals.length - 1].end : null;

  return {
    tasks,
    events,
    loadedStart,
    loadedEnd,
    isLoading,
    isExtending,
    error,
    extendBack,
    extendForward,
    ensureRange,
    reload,
  };
}
